// Format a Phoenix-validation error response (`{error: "validation",
// details: %{field => [msg, ...]}}`) into a human-readable banner
// string. Shared by every admin endpoint that surfaces Ecto-style
// changeset errors — currently the NPC-deck CRUD flow.
//
// Input shape mirrors `Ecto.Changeset.traverse_errors/2` after
// JSON-encoding: `details` is `Record<field, string[]>`. We tolerate
// non-array values and missing details to stay friendly when an
// upstream change accidentally narrows the error shape.

import { ApiError } from "../api/client";

export function formatChangesetErrors(body: unknown): string {
  if (!body || typeof body !== "object" || !("details" in body)) {
    return "Validation failed";
  }
  const details = (body as { details: unknown }).details;
  if (!details || typeof details !== "object") return "Validation failed";

  const parts: string[] = [];
  for (const [field, msgs] of Object.entries(details)) {
    if (Array.isArray(msgs)) {
      for (const m of msgs) parts.push(`${field}: ${m}`);
    }
  }
  return parts.length === 0 ? "Validation failed" : parts.join(" · ");
}

/** Try to extract a changeset-error message from an error; fall back
 *  to the error's plain message. Use this from `catch` blocks that
 *  want one-line "what went wrong" copy. */
export function describeError(err: unknown): string {
  if (
    err instanceof ApiError &&
    err.body &&
    typeof err.body === "object" &&
    "details" in err.body
  ) {
    return formatChangesetErrors(err.body);
  }
  if (err instanceof ApiError) {
    if (err.body && typeof err.body === "object" && "error" in err.body) {
      const code = String((err.body as { error: unknown }).error);
      const detail =
        "detail" in err.body
          ? ` (${JSON.stringify((err.body as { detail: unknown }).detail)})`
          : "";
      return `${code}${detail}`;
    }
    return `HTTP ${err.status ?? "?"}: ${err.message}`;
  }
  return err instanceof Error ? err.message : String(err);
}
